import {
  Description,
  Label,
  Radio,
  RadioGroup,
  Switch,
} from "@headlessui/react";
import {
  TrashIcon,
  ChatAlt2Icon,
  MicrophoneIcon,
  VideoCameraIcon,
  DesktopComputerIcon,
  ReceiptTaxIcon,
  ShoppingCartIcon,
  ChevronRightIcon,
  CreditCardIcon,
  InboxIcon,
  TagIcon,
  DocumentTextIcon,
  AcademicCapIcon,
  RefreshIcon,
  OfficeBuildingIcon,
  PresentationChartBarIcon,
} from "@heroicons/react/outline";
import { CheckCircleIcon, ThumbUpIcon } from "@heroicons/react/solid";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import FormPhoneInput from "../../Components/v2/Form/FormPhoneInput";
import { useNavigate } from "react-router-dom";
import { Alert } from "../../Components/Alert";
import { Button } from "../../Components/Button";
import { EmptyList } from "../../Components/EmptyList";
import { ErrorList } from "../../Components/ErrorList";
import { Modal } from "../../Components/Modal";
import {
  addLocalNotification,
  requestGetPointBalance,
  useAuthState,
  useNotificationDispatch,
  usePointDispatch,
  usePointState,
} from "../../Context";
import {
  getCartProducts,
  requestPaymentMethods,
  removeFromCart,
  requestListVoucher,
  requestGetVoucher,
  clearVoucher,
  pay,
  clearCart,
  requestListRedeemedVoucher,
  clearPayError,
} from "../../Context/checkout/actions";
import {
  useCheckoutDispatch,
  useCheckoutState,
} from "../../Context/checkout/context";
import {
  classNames,
  convertToGoogleAnalyticsItem,
  currencyFormat,
  getErrorMessages,
  getVoucherCategory,
  trackInitiateCheckout,
} from "../../Utils";
import { analytics } from "../../firebase";
import { logEvent } from "firebase/analytics";
import PointIcon from "../../Components/PointIcon";
import { calculatePaymentSummary } from "./paymentSummary";
import { COLLAPSED_PRODUCT_COUNT, getVisibleProducts } from "./visibleProducts";

// Mobile membebaskan biaya multi e-billing hanya untuk tiga tier ini; tier lain
// ditagih Rp1.000 per e-billing.
const EBILLING_FEE_EXEMPT_MEMBERSHIPS = ["PREMIUM", "CONSULTANT", "PRIORITY"];

// Add-on konsultasi mewarisi `detail` dari konsultasi utamanya, jadi nama
// konsultan dan deskripsi user cukup muncul sekali di baris konsultasinya.
function isMainConsultation(product) {
  return (
    ["consultation", "consultation-extend"].includes(product.type) &&
    !product.id?.includes("addon") &&
    !/^(video-|zoom-)/.test(product.id || "")
  );
}

function isConsultationAddon(product) {
  return (
    ["consultation", "consultation-extend"].includes(product.type) &&
    !isMainConsultation(product)
  );
}

function getPaymentLogo(method) {
  return method?.imageSvg || method?.image;
}

function getTotalPrice(products) {
  let total = 0;
  products.forEach((product) => {
    total += product.price * product.quantity;
  });
  return total;
}

const Checkout = () => {
  const navigate = useNavigate();
  const notificationDispatch = useNotificationDispatch();
  const { t } = useTranslation();

  const dispatch = useCheckoutDispatch();
  const {
    allProducts,
    transactionId,
    voucher,
    voucherValid,
    voucherLoading,
    voucherError,
    vouchers,
    paymentStatus,
    error,
    payError,
    loading,
    subscriptionVouchers,
    redeemedVouchers,
    redeemedVoucherLoading,
    redeemedVoucherError,
  } = useCheckoutState();

  const pointDispatch = usePointDispatch();
  const { loading: pointLoading, balance, error: pointError } = usePointState();

  const { user } = useAuthState();

  const [selectPaymentMethodsModal, setselectPaymentMethodsModal] =
    useState(false);
  const [selectVouchersModal, setselectVouchersModal] = useState(false);
  const [selectedPaymentMethod, setselectedPaymentMethod] = useState(null);
  const [code, setcode] = useState("");
  const [selectedVoucher, setselectedVoucher] = useState(null);
  const [productType, setproductType] = useState("");
  const [allowedDiscountProducts, setallowedDiscountProducts] = useState([]);
  const [totalDiscount, settotalDiscount] = useState(0);
  const [multiEbillingFee, setmultiEbillingFee] = useState(0);
  const [phone, setphone] = useState("");
  const [phoneValidationMessage, setphoneValidationMessage] = useState("");
  const subtotal = getTotalPrice(allProducts);
  const subtotalForDiscount = getTotalPrice(allowedDiscountProducts);
  const [usePoint, setusePoint] = useState(false);
  const [isInsufficientPoint, setisInsufficientPoint] = useState(false);
  const [viewAll, setviewAll] = useState(false);

  // Yang dibayar sebelum biaya metode: tiap metode menghitung biayanya sendiri
  // dari angka ini.
  const paymentAmount = useMemo(
    () => Math.max(subtotal - totalDiscount + multiEbillingFee, 0),
    [subtotal, totalDiscount, multiEbillingFee],
  );

  const pointUsageEnabled =
    usePoint && Boolean(paymentStatus.point_rates?.usage);

  const { total, belowMinimumFee, transactionFee, pointUsed, uniqueCode } =
    useMemo(
      () =>
        calculatePaymentSummary({
          amount: paymentAmount,
          paymentMethod: selectedPaymentMethod,
          pointBalance: balance.balance,
          productType,
          usePoint: pointUsageEnabled,
        }),
      [
        paymentAmount,
        selectedPaymentMethod,
        balance.balance,
        productType,
        pointUsageEnabled,
      ],
    );

  // Tiap metode dinilai pakai totalnya sendiri. Kalau dinilai pakai total metode
  // yang sedang dipilih, biaya metode itu bisa mendorong total melewati limit
  // metode lain sehingga metode tersebut hilang dari daftar.
  const paymentOptions = useMemo(() => {
    if (!paymentStatus?.methods) {
      return [];
    }
    return paymentStatus.methods
      .map((method) => ({
        method,
        summary: calculatePaymentSummary({
          amount: paymentAmount,
          paymentMethod: method,
          pointBalance: balance.balance,
          productType,
          usePoint: pointUsageEnabled,
        }),
      }))
      .filter(({ method, summary }) =>
        method.limit == null ? true : summary.total <= Number(method.limit),
      );
  }, [
    paymentStatus?.methods,
    paymentAmount,
    balance.balance,
    productType,
    pointUsageEnabled,
  ]);

  const visibleProducts = useMemo(
    () => getVisibleProducts(allProducts, viewAll),
    [viewAll, allProducts],
  );

  const selectedPaymentOption = useMemo(
    () =>
      paymentOptions.find(
        ({ method }) => method.id === selectedPaymentMethod?.id,
      ),
    [paymentOptions, selectedPaymentMethod?.id],
  );

  const pointEarned = useMemo(() => {
    if (paymentStatus.point_rates?.rate) {
      const {
        rate,
        premium_multiplier: premiumMultiplier,
        maximum_point: maxPoint,
      } = paymentStatus.point_rates;
      let point = Math.floor(total * rate);
      if (user.membership === "PREMIUM") {
        point = Math.floor(point * premiumMultiplier);
      }
      if (point > maxPoint) {
        point = maxPoint;
      }
      return point;
    }
    return 0;
  }, [total, paymentStatus.point_rates, user.membership]);

  const parsedErrorResponse = payError?.response?.body
    ? JSON.parse(payError.response.body)
    : null;
  const payErrorCode = parsedErrorResponse?.code;
  const pointDisabled = pointError || balance.balance <= 0 || pointLoading;
  const phoneRequired =
    (!user?.phone && selectedPaymentMethod?.verified_number_only) ||
    payErrorCode === "PHONE_REQUIRED";

  const usableVouchers = useMemo(() => {
    // Tanpa daftar voucher terpakai, limit redeem tidak bisa dihitung — lebih
    // baik tidak menawarkan apa pun daripada menawarkan voucher yang kuotanya
    // sudah habis dan baru ditolak server saat bayar.
    if (redeemedVoucherError) {
      return [];
    }

    const redemptionCounts = redeemedVouchers?.reduce((acc, voucher) => {
      if (vouchers.some((v) => v.id === voucher.voucherID)) {
        acc[voucher.voucherID] = (acc[voucher.voucherID] || 0) + 1;
      }
      return acc;
    }, {});

    // Daftar diambil per kategori, jadi filter ini hanya berarti saat keranjang
    // yang tipenya baru berubah masih memegang voucher tipe sebelumnya. Kategori
    // harus dipetakan dulu: keranjang mixed e-billing memakai voucher `general`,
    // bukan voucher bernama `mixed-ebilling` yang tidak pernah ada. Voucher tanpa
    // kategori berlaku di mana saja.
    const category = getVoucherCategory(productType);

    return vouchers.filter((voucher) => {
      const redeemedCount = redemptionCounts[voucher.id] || 0;
      const isCategoryMatch = !voucher.category || voucher.category === category;
      return redeemedCount < voucher.limit && isCategoryMatch;
    });
  }, [redeemedVoucherError, redeemedVouchers, vouchers, productType]);

  useEffect(() => {
    getCartProducts(dispatch);
  }, [dispatch]);

  useEffect(() => {
    if (transactionId) {
      const analyticsParams = {
        currency: "IDR",
        value: total,
        items: allProducts.map((product) =>
          convertToGoogleAnalyticsItem(product),
        ),
      };

      if (code) {
        analyticsParams.coupon = code;
      }

      clearCart(dispatch);
      logEvent(analytics, "begin_checkout", analyticsParams);

      trackInitiateCheckout({
        products: allProducts,
      });

      navigate(`/transaction/${transactionId}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, dispatch, transactionId]);

  useEffect(() => {
    if (productType !== "" && allProducts.length > 0 && user.id) {
      requestListVoucher(dispatch, { userID: user.id, productType, subtotal });
      requestListRedeemedVoucher(dispatch, { userID: user.id });
      requestPaymentMethods(dispatch, { productType });
    }
  }, [dispatch, productType, allProducts, subtotal, user.id]);

  useEffect(() => {
    if (user.membership && allProducts.length > 0) {
      let newProductType = "";
      let allowedProducts = [];
      let allEbillings = [];

      allProducts.forEach((product) => {
        let currentProductType = product.type;
        if (currentProductType === "consultation-extend") {
          currentProductType = "consultation";
        }
        // Limit ebilling and emeterai payment
        if (newProductType === "") {
          // Initial product type
          newProductType = currentProductType;
        } else if (newProductType !== currentProductType) {
          // Can't change if already mixed-ebilling
          if (newProductType === "mixed-ebilling") {
          } else {
            if (currentProductType === "ebilling") {
              // ebilling exist in products, change to mixed-ebilling
              newProductType = "mixed-ebilling";
            } else if (newProductType === "ebilling") {
              // ebilling mixed with other products, change to mixed-ebilling
              newProductType = "mixed-ebilling";
            } else if (currentProductType === "emeterai") {
              // emeterai exist in products, change to mixed-emeterai
              newProductType = "mixed-emeterai";
            } else if (newProductType === "emeterai") {
              // emeterai mixed with other products, change to mixed-emeterai
              newProductType = "mixed-emeterai";
            } else if (
              newProductType !== "ebilling" &&
              newProductType !== "mixed-ebilling" &&
              newProductType !== "emeterai" &&
              newProductType !== "mixed-emeterai"
            ) {
              // mixed products without ebilling and emeterai
              newProductType = "general";
            }
          }
        }
        if (currentProductType === "business-service-addon") {
          newProductType = "business-service";
        }
        switch (product.type) {
          case "consultation":
            if (product.id === "online-consultation") {
              allowedProducts.push(product);
            }
            break;
          case "consultation-extend":
            if (!product.id.startsWith("video-")) {
              allowedProducts.push(product);
            }
            break;
          case "subscription":
            allowedProducts.push(product);
            break;
          case "ebilling":
            allEbillings.push(product);
            break;
          default:
            allowedProducts.push(product);
            break;
        }
      });

      const perEbillingFee = EBILLING_FEE_EXEMPT_MEMBERSHIPS.includes(
        user.membership,
      )
        ? 0
        : 1000;
      const totalEbillingFee = allEbillings.length * perEbillingFee;
      // Satu e-billing tetap gratis; biaya baru berlaku mulai e-billing kedua.
      setmultiEbillingFee(totalEbillingFee > 1000 ? totalEbillingFee : 0);

      setproductType(newProductType);
      setallowedDiscountProducts(allowedProducts);
    }
  }, [allProducts, user.membership]);

  useEffect(() => {
    if (checkMembershipAndVoucher()) {
      for (let index = 0; index < subscriptionVouchers.length; index++) {
        const subscriptionVoucher = subscriptionVouchers[index];
        if (subscriptionVoucher.category === productType) {
          setselectedVoucher(subscriptionVoucher);
          setcode(subscriptionVoucher.id);
          applyCode(subscriptionVoucher.id);
          break;
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.membership, subscriptionVouchers]);

  useEffect(() => {
    let discount = 0;
    if (voucherValid && voucher) {
      const discountType = voucher.type;
      const discountTotal = parseInt(voucher.total, 10);
      const maxDiscount = parseInt(voucher.maxDiscount || 0, 10);

      if (discountType === "AMOUNT") {
        discount = discountTotal;
      } else if (discountType === "PERCENTAGE") {
        discount = (subtotalForDiscount * discountTotal) / 100;
      }

      if (maxDiscount > 0 && discount > maxDiscount) {
        discount = maxDiscount;
      }

      discount = Math.floor(discount);
    }

    settotalDiscount(discount);
  }, [voucher, voucherValid, subtotalForDiscount]);

  useEffect(() => {
    if (payError) {
      let message = "Error processing payment.";

      if (payError?.message) {
        message = payError.message;
      }

      if (parsedErrorResponse) {
        if (parsedErrorResponse.message) {
          message = parsedErrorResponse.message;
        }
      }

      if (payErrorCode === "INSUFFICIENT_POINT") {
        setusePoint(false);
        setisInsufficientPoint(true);
      }

      addLocalNotification(notificationDispatch, {
        id: `payError-${Math.floor(Date.now() / 1000)}`,
        type: "error",
        title: "Error",
        body: message,
        showFull: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payError]);

  useEffect(() => {
    if (
      selectedPaymentMethod?.id &&
      !paymentOptions.some(
        ({ method }) => method.id === selectedPaymentMethod.id,
      )
    ) {
      setselectedPaymentMethod(null);
    }
  }, [paymentOptions, selectedPaymentMethod?.id]);

  useEffect(() => {
    clearPayError(dispatch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPaymentMethod]);

  useEffect(() => {
    onPointLoad();

    return () => {
      clearPayError(dispatch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Kolom telepon juga muncul lewat PHONE_REQUIRED, di mana user sebenarnya
  // sudah punya nomor tersimpan — isi dari profil supaya tidak diketik ulang.
  useEffect(() => {
    if (phoneRequired && !phone && user?.phone) {
      setphone(user.phone);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneRequired, user?.phone]);

  const checkMembershipAndVoucher = () => {
    return Boolean(
      user.membership === "PREMIUM" && subscriptionVouchers?.length > 0,
    );
  };

  const onPointLoad = () => {
    setisInsufficientPoint(false);
    requestGetPointBalance(pointDispatch);
  };

  function removeProduct(product) {
    const analyticsParams = {
      currency: "IDR",
      value: product.price,
      items: [convertToGoogleAnalyticsItem(product)],
    };
    logEvent(analytics, "remove_from_cart", analyticsParams);
    removeFromCart(dispatch, product);
  }

  function clearSelectedVoucher() {
    setselectedVoucher(null);
    setcode("");
    clearVoucher(dispatch);
  }

  function applyCode(id) {
    const matched = usableVouchers.find((item) => item.id === id);
    if (matched) {
      setselectedVoucher(matched);
    }
    requestGetVoucher(dispatch, { id, user, productType });
  }

  function onPay() {
    // Arahkan, jangan sekadar disable: tombol yang abu-abu tanpa alasan tidak
    // memberi tahu user apa yang kurang.
    if (!selectedPaymentMethod?.id && total > 0) {
      setselectPaymentMethodsModal(true);
      return;
    }

    if (phoneRequired && phone.length < 10) {
      setphoneValidationMessage("Enter a valid phone number");
      return;
    }

    const payload = {
      products: allProducts,
      paymentMethod: total === 0 ? "voucher" : selectedPaymentMethod?.id,
      voucher: code,
    };

    if (phone) {
      payload.phone = phone;
    }

    if (usePoint && paymentStatus.point_rates?.usage && balance.balance) {
      payload.usePoint = pointUsed;
    }

    pay(dispatch, payload);
  }

  if (allProducts.length === 0)
    return (
      <div className="bg-white min-h-full rounded-md sm:rounded-lg">
        <div className="max-w-lg mx-auto p-8 text-center">
          <EmptyList icon={ShoppingCartIcon} title="Your cart is empty" />
          <div className="mt-4 flex justify-center">
            <Button outline title="Order Again" onClick={() => navigate(-1)} />
          </div>
        </div>
      </div>
    );

  function renderVoucherSkeleton() {
    return (
      <div className="animate-pulse divide-y divider-gray-300">
        <div className="p-4 flex items-center">
          <div className="h-6 w-[20%] my-1.5 mr-2 bg-gray-300 rounded-md"></div>
          <div className="h-4 w-[80%] my-1.5 bg-gray-300 rounded-md"></div>
        </div>
        <div className="p-4 flex items-center">
          <div className="h-6 w-[20%] my-1.5 mr-2 bg-gray-300 rounded-md"></div>
          <div className="h-4 w-[80%] my-1.5 bg-gray-300 rounded-md"></div>
        </div>
        <div className="p-4 flex items-center">
          <div className="h-6 w-[20%] my-1.5 mr-2 bg-gray-300 rounded-md"></div>
          <div className="h-4 w-[80%] my-1.5 bg-gray-300 rounded-md"></div>
        </div>
      </div>
    );
  }

  const renderPaymentModal = () => (
    <Modal
      title="Select Payment Method"
      show={selectPaymentMethodsModal}
      onClose={() => setselectPaymentMethodsModal(false)}
    >
      {loading ? (
        renderVoucherSkeleton()
      ) : (
        <div>
          {error ? (
            <ErrorList
              className="p-4"
              title="Error"
              message={error?.message || "Error fetching payment methods."}
              errors={error?.errors}
            />
          ) : (
            <div>
              {paymentOptions.length > 0 ? (
                <div className="divide-y divider-gray-300">
                  <div className="flex items-center justify-between bg-blue-50 px-4 py-3">
                    <div className="flex flex-col text-left mr-3">
                      <span className="text-sm text-gray-500">
                        {t("Estimated Total")}
                      </span>
                      <span className="text-xs text-gray-400">
                        {selectedPaymentOption
                          ? selectedPaymentOption.method.name
                          : t("Select payment method")}
                      </span>
                    </div>
                    <span
                      data-testid="estimated-total"
                      className="text-base font-semibold text-gray-900 whitespace-nowrap"
                    >
                      {currencyFormat(
                        selectedPaymentOption?.summary.total ?? paymentAmount,
                      )}
                    </span>
                  </div>
                  {paymentOptions.map(({ method, summary }) => (
                    <button
                      data-testid="select-payment-method"
                      key={method.id}
                      className="w-full"
                      onClick={() => {
                        setselectPaymentMethodsModal(false);
                        setselectedPaymentMethod(method);
                      }}
                    >
                      <div className="flex items-center justify-between p-4">
                        <div className="flex flex-1 text-left">
                          {getPaymentLogo(method) ? (
                            <img
                              src={getPaymentLogo(method)}
                              className="object-contain w-16 mr-4"
                              alt="payment-logo"
                            />
                          ) : (
                            <div className="flex w-16 mr-4 items-center justify-center">
                              <CreditCardIcon className="h-6 w-6 text-emerald-700" />
                            </div>
                          )}
                          <div className="flex flex-col">
                            <span>{method.name}</span>
                            <span
                              data-testid={`payment-fee-${method.id}`}
                              className={
                                summary.additionalFee > 0 || summary.uniqueCode
                                  ? "text-xs text-gray-500"
                                  : "text-xs text-emerald-700"
                              }
                            >
                              {/* Kode unik bukan biaya tetap, jadi yang
                                  ditampilkan rentangnya — bukan "tanpa biaya
                                  tambahan", yang menyesatkan karena nominal
                                  transfernya tetap berubah. */}
                              {summary.uniqueCode
                                ? `${t("Unique Code Fee")} Rp${
                                    summary.uniqueCode.minimum
                                  } ~ Rp${summary.uniqueCode.maximum}`
                                : summary.additionalFee > 0
                                  ? `${t("Fee")} ${currencyFormat(
                                      summary.additionalFee,
                                    )}`
                                  : t("No additional fee")}
                            </span>
                          </div>
                        </div>
                        <div>
                          {selectedPaymentMethod?.id === method?.id ? (
                            <CheckCircleIcon className="h-5 w-5 text-blue-600" />
                          ) : (
                            <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <EmptyList
                  className="p-4"
                  title="No Payment Method Available"
                />
              )}
            </div>
          )}
        </div>
      )}
    </Modal>
  );

  const renderVoucherModal = () => {
    return (
      <Modal
        title="Select Voucher"
        show={selectVouchersModal}
        onClose={() => setselectVouchersModal(false)}
        footer={
          <div className="p-4 flex flex-row-reverse justify-between items-center border-t border-gray-300 bg-gray-50 rounded-b-lg">
            <Button
              loading={voucherLoading || redeemedVoucherLoading}
              disabled={voucherLoading || redeemedVoucherLoading}
              onClick={() => {
                setselectVouchersModal(false);
              }}
              className="w-24"
              title="Continue"
            />
            {code && voucherValid && (
              <div>
                <p className="text-xs text-slate-500">{code}</p>
                {voucherLoading ? (
                  <div className="animate-pulse h-3 mt-[2px] mb-[2px] w-16 bg-gray-300 rounded-md"></div>
                ) : (
                  <p className="text-xs font-medium text-green-600">
                    <NumericFormat
                      displayType={"text"}
                      value={totalDiscount}
                      thousandSeparator={"."}
                      decimalSeparator={","}
                      prefix={"Rp"}
                    />
                  </p>
                )}
              </div>
            )}
          </div>
        }
      >
        <div className="divide-y divider-gray-300">
          <div className="p-4 space-y-2">
            <div className="flex gap-2">
              <input
                type="text"
                id="discount-code"
                name="discount-code"
                className={classNames(
                  voucherError ? "border-red-500" : "border-gray-300",
                  "block w-full text-sm rounded-md shadow-sm disabled:bg-slate-50 disabled:text-slate-500 disabled:border-slate-200 focus:ring-blue-500 focus:border-blue-500",
                )}
                placeholder={t("enterVoucherOrReferralCode")}
                value={code}
                disabled={voucherValid}
                onChange={(e) => setcode(e.target.value.toUpperCase())}
              />
              <Button
                loading={voucherLoading || redeemedVoucherLoading}
                disabled={
                  voucherLoading || redeemedVoucherLoading || code === ""
                }
                className="w-24"
                onClick={() => {
                  if (voucherValid) {
                    clearSelectedVoucher();
                  } else {
                    applyCode(code);
                  }
                }}
                outline={voucherValid}
                title={voucherValid ? "Clear" : "Apply"}
                color={voucherValid ? "red" : "secondary"}
              />
            </div>
            <div>
              {voucherValid && (
                <Alert
                  type="success"
                  title="Voucher Applied"
                  message={`${t("You saved")} ${currencyFormat(totalDiscount)}`}
                />
              )}
              {voucherError && (
                <Alert
                  className="mt-2"
                  type="error"
                  message={voucherError?.message || "Error applying voucher."}
                  errors={voucherError?.errors}
                />
              )}
            </div>
          </div>
          <RadioGroup
            value={selectedVoucher}
            onChange={(voucher) => {
              setselectedVoucher(voucher);
              setcode(voucher.id);
              applyCode(voucher.id);
            }}
          >
            {usableVouchers.length > 0 ? (
              <div className="space-y-4 p-4">
                {usableVouchers.map((voucher, index) => {
                  let bestDiscount = false;
                  if (index === 0 && usableVouchers.length > 1) {
                    bestDiscount = true;
                  }

                  let totalSaved = voucher.total;
                  if (voucher.type === "PERCENTAGE") {
                    totalSaved = (subtotalForDiscount * voucher.total) / 100;
                    if (
                      voucher.maxDiscount &&
                      totalSaved > voucher.maxDiscount
                    ) {
                      totalSaved = voucher.maxDiscount;
                    }
                  }

                  return (
                    <Radio
                      key={voucher.id}
                      value={voucher}
                      className={({ checked }) =>
                        classNames(
                          checked ? "border-blue-600" : "border-gray-300",
                          "relative border bg-white rounded-md sm:rounded-lg shadow-sm p-4 flex cursor-pointer hover:bg-slate-50 focus:outline-none data-[focus]:ring-2 data-[focus]:ring-blue-600 data-[focus]:ring-offset-2",
                        )
                      }
                    >
                      {({ checked }) => (
                        <div className="w-full">
                          <div className="flex justify-between items-center">
                            <Label
                              as="div"
                              className="block text-sm font-medium text-gray-900"
                            >
                              {voucher.id}
                            </Label>
                            <CheckCircleIcon
                              className={classNames(
                                !checked ? "invisible" : "",
                                "h-5 w-5 text-blue-600",
                              )}
                              aria-hidden="true"
                            />
                          </div>
                          <Description
                            as="span"
                            className="mt-1 flex items-center text-sm text-gray-500"
                          >
                            {t(voucher.description)}
                          </Description>
                          <div className="mt-6 flex justify-between items-center flex-wrap gap-1">
                            <Description
                              as="div"
                              className="text-sm font-medium text-gray-900"
                            >
                              <span>
                                {voucher.type === "PERCENTAGE" ? (
                                  `${voucher.total}%`
                                ) : (
                                  <NumericFormat
                                    displayType={"text"}
                                    value={voucher.total}
                                    thousandSeparator={"."}
                                    decimalSeparator={","}
                                    prefix={"Rp"}
                                  />
                                )}
                              </span>
                            </Description>
                            <div className="flex justify-end items-center gap-1 flex-wrap">
                              {bestDiscount && (
                                <Description
                                  as="div"
                                  className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-yellow-100 text-yellow-800 shrink-0"
                                >
                                  <ThumbUpIcon
                                    className="h-3 w-3 mr-1"
                                    aria-hidden="true"
                                  />
                                  <span className="text-xs font-medium">
                                    {t("Best Voucher")}
                                  </span>
                                </Description>
                              )}
                              <Description
                                as="div"
                                className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-green-100 text-green-800 shrink-0"
                              >
                                <span className="text-xs font-medium">
                                  {t("Saving")}{" "}
                                  <NumericFormat
                                    displayType={"text"}
                                    value={totalSaved}
                                    thousandSeparator={"."}
                                    decimalSeparator={","}
                                    prefix={"Rp"}
                                  />
                                </span>
                              </Description>
                            </div>
                          </div>
                        </div>
                      )}
                    </Radio>
                  );
                })}
              </div>
            ) : (
              <div className="flex-1 flex flex-col justify-center text-center p-4">
                <InboxIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  {t("No available voucher")}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {t("Enter your voucher code manually")}
                </p>
              </div>
            )}
          </RadioGroup>
        </div>
      </Modal>
    );
  };

  let payButtonTitle = "Pay Now";
  if (!selectedPaymentMethod?.id && total > 0) {
    payButtonTitle = "Select Method";
  } else if (total === 0) {
    payButtonTitle = "Create Order";
  }

  return (
    <div>
      {/* Modal */}
      {renderPaymentModal()}
      {renderVoucherModal()}

      <div className="grid gird-cols-1 lg:grid-cols-2 gap-y-4 lg:gap-x-8">
        <div className="bg-white border border-gray-200 rounded-md sm:rounded-lg shadow-sm">
          <h3 className="sr-only">Items in your cart</h3>
          <ul className="divide-y divide-gray-200">
            {visibleProducts.map((product, index) => (
              <li
                key={`${product.id}-${index}`}
                className={`flex py-6 px-4 sm:px-6 ${
                  product.detail?.unavailable ? "opacity-60" : ""
                }`}
              >
                <div className="flex-shrink-0">
                  {product.type === "consultation" ||
                  product.type === "consultation-extend" ? (
                    product.id?.endsWith("audio-addon-v3") ? (
                      <MicrophoneIcon className="h-8 w-8 text-blue-600" />
                    ) : product.id?.includes("video-addon") ? (
                      <VideoCameraIcon className="h-8 w-8 text-blue-600" />
                    ) : product.id?.includes("zoom-addon") ? (
                      <DesktopComputerIcon className="h-8 w-8 text-blue-600" />
                    ) : (
                      <ChatAlt2Icon className="h-8 w-8 text-blue-600" />
                    )
                  ) : null}
                  {product.type === "ebilling" ? (
                    <ReceiptTaxIcon className="h-8 w-8 text-blue-600" />
                  ) : null}
                  {product.type === "subscription" ? (
                    <TagIcon className="h-8 w-8 text-blue-600" />
                  ) : null}
                  {product.type === "emeterai" ? (
                    <DocumentTextIcon className="h-8 w-8 text-blue-600" />
                  ) : null}
                  {product.type === "scheduled-tutoring" ? (
                    <AcademicCapIcon className="h-8 w-8 text-blue-600" />
                  ) : null}
                  {product.type === "business-service" ||
                  product.type === "business-service-addon" ? (
                    <OfficeBuildingIcon className="h-8 w-8 text-blue-600" />
                  ) : null}
                  {product.type === "webinar" ? (
                    <PresentationChartBarIcon className="h-8 w-8 text-blue-600" />
                  ) : null}
                </div>

                <div className="ml-6 flex-1 flex flex-col">
                  <div className="flex">
                    <div className="min-w-0 flex-1">
                      <h4 className="text-sm">{t(product.name)}</h4>
                      {(product.detail?.category ||
                        product.detail?.categoryName) && (
                        <p className="mt-1 text-sm text-gray-500">
                          {t(
                            product.detail.category ||
                              product.detail.categoryName,
                          )}
                        </p>
                      )}
                      {!isConsultationAddon(product) &&
                        product.detail?.description && (
                          <p className="mt-1 text-sm text-gray-500">
                            {t(product.detail.description)}
                          </p>
                        )}
                      {isMainConsultation(product) &&
                        product.detail?.consultantName && (
                          <p className="mt-1 text-sm font-medium text-gray-700">
                            {product.detail.consultantName}
                          </p>
                        )}
                      {product.detail?.useGlobalBalance && (
                        <p className="mt-1 text-sm text-gray-500">
                          {t("Using Video Call Balance")}
                        </p>
                      )}
                      {product.detail?.unavailable && (
                        <p className="mt-1 text-sm text-red-600">
                          {t(
                            "This video add-on is currently unavailable. Please remove it to continue.",
                          )}
                        </p>
                      )}
                    </div>

                    <div className="ml-4 flex-shrink-0 flow-root">
                      <button
                        type="button"
                        onClick={() => {
                          removeProduct(product);
                        }}
                        className="-m-2.5 bg-white p-2.5 flex items-center justify-center text-gray-400 hover:text-gray-500"
                      >
                        <span className="sr-only">Remove</span>
                        <TrashIcon className="h-5 w-5" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                  <div className="flex-1 pt-2 flex items-end justify-between">
                    <p className="mt-1 text-sm font-medium text-gray-900">
                      <NumericFormat
                        displayType={"text"}
                        value={product.price * product.quantity}
                        thousandSeparator={"."}
                        decimalSeparator={","}
                        prefix={"Rp"}
                      />
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
          {allProducts.length > COLLAPSED_PRODUCT_COUNT && (
            <button
              data-testid="toggle-view-all-products"
              type="button"
              onClick={() => setviewAll((prevViewAll) => !prevViewAll)}
              className="w-full border-t border-gray-200 px-4 py-3 text-sm font-medium text-blue-600 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
            >
              {viewAll
                ? t("Collapse List")
                : `${t("View All")} (${allProducts.length})`}
            </button>
          )}
        </div>
        <div>
          <div className="bg-white border border-gray-200 rounded-md sm:rounded-lg shadow-sm">
            {error?.message && (
              <div className="border-b border-gray-200 p-4 text-center">
                <ErrorList
                  title="Checkout cannot be loaded"
                  message={error.message}
                  errors={error?.errors}
                />
                <div className="mt-4 flex justify-center">
                  <Button
                    data-testid="retry-checkout"
                    disabled={loading}
                    title="Try Again"
                    onClick={() => {
                      clearVoucher(dispatch);
                      requestListVoucher(dispatch, {
                        userID: user.id,
                        productType,
                        subtotal,
                      });
                      requestListRedeemedVoucher(dispatch, { userID: user.id });
                      requestPaymentMethods(dispatch, { productType });
                    }}
                  />
                </div>
              </div>
            )}
            <div className="p-4 space-y-4">
              <button
                data-testid="payment-method-modal"
                onClick={() => setselectPaymentMethodsModal(true)}
                className="inline-flex w-full items-center justify-between px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md bg-white hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
              >
                <div className="flex justify-start items-center">
                  <CreditCardIcon className="w-8 h-8 mr-2 text-blue-600 shrink-0" />
                  <div className="flex flex-col items-start text-left space-y-1">
                    <span className="text-xs text-gray-500">
                      {t("Payment Method")}
                    </span>
                    <span
                      className={classNames(
                        selectedPaymentMethod?.name
                          ? "text-black"
                          : "text-gray-400",
                        "text-sm",
                      )}
                    >
                      {selectedPaymentMethod?.name ||
                        t("Select payment method")}
                    </span>
                    {!selectedPaymentMethod?.id && total > 0 && (
                      <span className="text-xs text-gray-500">
                        {t("Required to continue")}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center">
                  {getPaymentLogo(selectedPaymentMethod) && (
                    <img
                      src={getPaymentLogo(selectedPaymentMethod)}
                      className="h-6 mr-2"
                      alt="payment-logo"
                    />
                  )}
                  <ChevronRightIcon className="w-5 h-5 text-gray-300" />
                </div>
              </button>
              <button
                onClick={() => setselectVouchersModal(true)}
                className="inline-flex w-full items-center justify-between px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md bg-white hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
              >
                <div className="flex justify-start items-center">
                  <ReceiptTaxIcon className="w-8 h-8 mr-2 text-teal-600 shrink-0" />
                  <div className="flex flex-col items-start text-left space-y-1">
                    <span className="text-xs text-gray-500">
                      {t("voucherOrReferralCode")}
                    </span>
                    <span
                      className={classNames(
                        code && voucherValid ? "text-black" : "text-gray-400",
                        "text-sm",
                      )}
                    >
                      {code && voucherValid
                        ? code
                        : t("Enter or select voucher")}
                    </span>
                  </div>
                </div>
                <div className="flex items-center">
                  {usableVouchers.length > 0 && !code && (
                    <span className="text-xs text-teal-600 mr-2">
                      {usableVouchers.length} {t("vouchers available!")}
                    </span>
                  )}
                  <ChevronRightIcon className="w-5 h-5 text-gray-300" />
                </div>
              </button>
              {paymentStatus.point_rates?.usage && (
                <div className="inline-flex w-full items-center justify-between px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md bg-white">
                  <div className="flex justify-start items-center">
                    <PointIcon className="mr-2 shrink-0" />
                    <div className="flex flex-col items-start text-left space-y-1">
                      <span className="text-xs text-gray-500">
                        {t("point")}
                      </span>
                      {pointLoading ? (
                        <div className="flex animate-pulse items-center w-full rounded-lg shrink-0">
                          <div className="my-1 h-3 w-20 rounded-md bg-gray-300"></div>
                        </div>
                      ) : (
                        <div>
                          {pointError ? (
                            <span className="text-sm text-red-500">
                              {t(getErrorMessages(pointError))}
                            </span>
                          ) : (
                            <span className="text-sm text-yellow-500">
                              {(balance.balance || 0).toLocaleString("id-ID")}{" "}
                              pts
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center">
                    {pointError || isInsufficientPoint ? (
                      <button onClick={onPointLoad} disabled={pointLoading}>
                        {pointLoading ? (
                          <div className="flex justify-center text-center">
                            <svg
                              role="status"
                              className="w-6 h-6 animate-spin text-blue-500"
                              viewBox="0 0 100 101"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              aria-hidden="true"
                            >
                              <path
                                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                fill="#E5E7EB"
                              />
                              <path
                                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                fill="currentColor"
                              />
                            </svg>
                          </div>
                        ) : (
                          <RefreshIcon className="h-6 w-6 text-blue-500" />
                        )}
                      </button>
                    ) : (
                      <Switch
                        checked={usePoint}
                        onChange={() => {
                          setusePoint((prevUsePoint) => !prevUsePoint);
                        }}
                        disabled={pointDisabled}
                        className={classNames(
                          usePoint ? "bg-yellow-500" : "bg-gray-200",
                          "relative inline-flex flex-shrink-0 h-6 w-11 disabled:cursor-not-allowed border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-0",
                        )}
                      >
                        <span
                          aria-hidden="true"
                          className={classNames(
                            usePoint ? "translate-x-5" : "translate-x-0",
                            pointDisabled ? "bg-gray-300" : "bg-white",
                            "pointer-events-none inline-block h-5 w-5 rounded-full shadow transform ring-0 transition ease-in-out duration-200",
                          )}
                        />
                      </Switch>
                    )}
                  </div>
                </div>
              )}
            </div>
            {phoneRequired && (
              <div className="border-t p-4">
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium text-gray-500"
                >
                  {t("Phone")} <span className="text-red-500">*</span>
                </label>
                <FormPhoneInput
                  name="phone"
                  id="phone"
                  data-testid="phone"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-0 focus:border-blue-500 sm:text-sm"
                  placeholder={t("Enter Phone Number")}
                  value={phone}
                  error={phoneValidationMessage}
                  onChange={(value) => {
                    setphone(value);
                    if (phoneValidationMessage) {
                      setphoneValidationMessage("");
                    }
                  }}
                />
              </div>
            )}
            <dl className="border-t text-sm font-medium text-gray-500 border-gray-200 p-4 space-y-4">
              <div className="flex justify-between">
                <dt>{t("Subtotal")}</dt>
                <dd className="text-gray-900">
                  {
                    <NumericFormat
                      displayType={"text"}
                      value={subtotal}
                      thousandSeparator={"."}
                      decimalSeparator={","}
                      prefix={"Rp"}
                    />
                  }
                </dd>
              </div>
              {totalDiscount > 0 ? (
                <div className="flex justify-between">
                  <dt className="flex flex-wrap">
                    <p className="mr-2">{t("Discount")}</p>
                    {voucherValid && (
                      <p className="rounded-full bg-green-100 text-xs text-green-800 py-0.5 px-2 tracking-wide">
                        {voucher.id}
                      </p>
                    )}
                  </dt>
                  <dd className="text-teal-600 whitespace-nowrap">
                    <NumericFormat
                      displayType={"text"}
                      value={-Math.abs(totalDiscount)}
                      thousandSeparator={"."}
                      decimalSeparator={","}
                      prefix={"Rp"}
                    />
                  </dd>
                </div>
              ) : null}
              {usePoint > 0 && paymentStatus.point_rates?.usage ? (
                <div className="flex justify-between">
                  <dt className="flex flex-wrap">
                    <p className="mr-2">{t("pointUsed")}</p>
                  </dt>
                  <dd className="text-teal-600 whitespace-nowrap">
                    <NumericFormat
                      displayType={"text"}
                      value={pointUsed > 0 ? -Math.abs(pointUsed) : pointUsed}
                      thousandSeparator={"."}
                      decimalSeparator={","}
                      prefix={"Rp"}
                    />
                  </dd>
                </div>
              ) : null}
              {multiEbillingFee > 0 ? (
                <div className="flex justify-between">
                  <dt>{t("Multi E-Billing Payment Fee")}</dt>
                  <dd className="text-gray-900">
                    {
                      <NumericFormat
                        displayType={"text"}
                        value={multiEbillingFee}
                        thousandSeparator={"."}
                        decimalSeparator={","}
                        prefix={"Rp"}
                      />
                    }
                  </dd>
                </div>
              ) : null}
              {transactionFee > 0 || uniqueCode ? (
                <div className="flex justify-between">
                  <dt>
                    {uniqueCode ? t("Unique Code Fee") : t("Transaction Fee")}
                  </dt>
                  <dd className="text-gray-900">
                    {uniqueCode ? (
                      `Rp${uniqueCode.minimum} ~ Rp${uniqueCode.maximum}`
                    ) : (
                      <NumericFormat
                        displayType={"text"}
                        value={transactionFee}
                        thousandSeparator={"."}
                        decimalSeparator={","}
                        prefix={"Rp"}
                      />
                    )}
                  </dd>
                </div>
              ) : null}
              {belowMinimumFee > 0 ? (
                <div className="flex justify-between">
                  <dt>{t("Below Minimum Fee")}</dt>
                  <dd className="text-gray-900">
                    {
                      <NumericFormat
                        displayType={"text"}
                        value={belowMinimumFee}
                        thousandSeparator={"."}
                        decimalSeparator={","}
                        prefix={"Rp"}
                      />
                    }
                  </dd>
                </div>
              ) : null}
              <div className="flex items-center justify-between border-t border-gray-200 text-gray-900 pt-4">
                <dt className="text-base">{t("Total")}</dt>
                <span className="text-base">
                  {uniqueCode ? (
                    <span>
                      <NumericFormat
                        displayType={"text"}
                        value={total + uniqueCode.minimum}
                        thousandSeparator={"."}
                        decimalSeparator={","}
                        prefix={"Rp"}
                      />{" "}
                      ~{" "}
                      <NumericFormat
                        displayType={"text"}
                        value={total + uniqueCode.maximum}
                        thousandSeparator={"."}
                        decimalSeparator={","}
                        prefix={"Rp"}
                      />
                    </span>
                  ) : (
                    <NumericFormat
                      displayType={"text"}
                      value={total}
                      thousandSeparator={"."}
                      decimalSeparator={","}
                      prefix={"Rp"}
                    />
                  )}
                </span>
              </div>
              {pointEarned > 0 && (
                <div className="border-t pt-4">
                  <Alert
                    type="success"
                    message={t("pointsEarning", {
                      point: pointEarned.toLocaleString("id-ID"),
                    })}
                  />
                </div>
              )}
            </dl>

            <div className="sticky bottom-0 flex items-center justify-between gap-4 border-t border-gray-200 bg-white p-4 rounded-b-md sm:rounded-b-lg">
              <div className="flex flex-col">
                <span className="text-xs text-gray-500">
                  {t("Total Payment")}
                </span>
                <span className="text-base font-semibold text-gray-900 whitespace-nowrap">
                  {currencyFormat(total)}
                </span>
              </div>
              <Button
                type="submit"
                data-testid="button-order"
                onClick={onPay}
                loading={loading}
                // Hanya kondisi yang tidak bisa diperbaiki user lewat tombol ini
                // yang membuatnya disabled; sisanya diarahkan dari onPay.
                disabled={
                  allProducts.some((p) => p.detail?.unavailable) || loading
                }
                title={payButtonTitle}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
